import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { PrazoAgrupado } from '@/utils/equiplexQualidade';
import { fmtDias, fmtUn } from '@/utils/painelFormat';
import { ALTURA_BARRA_HORIZONTAL, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import TooltipBox from '../TooltipBox';

/** Prazo médio de liberação por máquina no recorte, do mais lento para o mais rápido. */
const PrazoMaquinaChart: FC<{ grupos: PrazoAgrupado[]; cores: CoresPainel }> = ({ grupos, cores }) => {
  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const g = active && payload?.[0] ? (payload[0].payload as PrazoAgrupado) : null;
    if (!g) return null;
    return (
      <TooltipBox
        titulo={g.rotulo}
        linhas={[
          { rotulo: 'Prazo médio', valor: fmtDias(g.prazoMedio), cor: cores.programado },
          { rotulo: '9 em 10 lotes em até', valor: fmtDias(g.prazoP90) },
          { rotulo: 'Lotes com prazo', valor: `${fmtUn(g.comPrazo)} de ${fmtUn(g.lotes)}` },
        ]}
      />
    );
  };

  return (
    <PainelCard testId="qualidade-prazo-maquina" titulo="Prazo médio de liberação por máquina" subtitulo="Da entrada no CQ à inspeção, em dias corridos.">
      {grupos.length === 0 ? (
        <div className="py-10 text-center text-sm text-content-muted">Nenhum lote liberado no recorte.</div>
      ) : (
        <div style={{ width: '100%', height: Math.max(180, grupos.length * ALTURA_BARRA_HORIZONTAL + 24) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grupos} layout="vertical" margin={{ top: 0, right: 56, bottom: 0, left: 8 }} barCategoryGap={6}>
              <CartesianGrid horizontal={false} stroke={cores.grade} />
              <XAxis type="number" tick={tickEixo(cores)} axisLine={false} tickLine={false} unit=" d" />
              <YAxis type="category" dataKey="rotulo" width={120} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="prazoMedio" fill={cores.programado} radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
                <LabelList
                  dataKey="prazoMedio"
                  position="right"
                  formatter={(v: unknown) => (typeof v === 'number' ? fmtDias(v) : '')}
                  style={{ fill: cores.texto, fontSize: FONTE_ROTULO, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PainelCard>
  );
};

export default PrazoMaquinaChart;
